import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "./PageShell";
import { Plus, Search, FileDown } from "lucide-react";

interface ProjectItem {
  id: string;
  function: string;
  projectName: string;
  status: string;
  startDate: string;
  endDate: string;
  level: string;
  type: string;
  owner: string;
  unit: string;
  totalBudget: string;
  fundingSource: string;
  allocatedBudget: string;
  allocationYear: string;
  unallocatedBudget: string;
  progress: string;
  reference: string;
}

const mockData: ProjectItem[] = [
  {
    id: "1",
    function: "- Chọn -",
    projectName: "Nghiên cứu phần mềm quản lý KHCN có ứng dụng AI »",
    status: "Đúng tiến độ",
    startDate: "12/2025",
    endDate: "12/2025",
    level: "Cấp Viện/ Chi nhánh Tỉnh/ TP: Hà Nội",
    type: "Đề tài",
    owner: "Thượng tá, VS. ThS Lê Anh Tuấn",
    unit: "Trung tâm Đào tạo và Hợp tác Quốc tế",
    totalBudget: "10.000",
    fundingSource: "Ngân sách sự nghiệp khoa học",
    allocatedBudget: "8.000",
    allocationYear: "2025",
    unallocatedBudget: "2.000",
    progress: "Đúng tiến độ",
    reference: "2017",
  },
  {
    id: "2",
    function: "- Chọn -",
    projectName: "NC »",
    status: "Đúng tiến độ",
    startDate: "10/2025",
    endDate: "06/2025",
    level: "Cấp Quốc gia",
    type: "Đề tài độc lập",
    owner: "Thượng tá, VS. ThS Lê Anh Tuấn",
    unit: "Viện Khoa học Thủy lợi Miền Nam",
    totalBudget: "25.000",
    fundingSource: "Ngân sách nhà nước",
    allocatedBudget: "15.000",
    allocationYear: "2025",
    unallocatedBudget: "10.000",
    progress: "Đúng tiến độ",
    reference: "2016",
  },
  {
    id: "3",
    function: "- Chọn -",
    projectName:
      "Điều tra, nghiên cứu, đánh giá thực trạng và nguy cơ lũ lụt, sạt lở đất lưu vực sông Sê Sanx và đề xuất các giải pháp phòng tránh và giải nhẹ »",
    status: "Đúng tiến độ",
    startDate: "01/2022",
    endDate: "12/2022",
    level: "Cấp Bộ",
    type: "Đề tài nghiên cứu",
    owner: "Thượng tá, GS. TS Nguyễn Thị Hạnh",
    unit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    totalBudget: "200",
    fundingSource: "Ngân sách sự nghiệp khoa học",
    allocatedBudget: "150",
    allocationYear: "2022",
    unallocatedBudget: "50",
    progress: "Đúng tiến độ",
    reference: "1016",
  },
];

const GenericPage: React.FC<{ title: string }> = ({ title }) => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  return (
    <PageShell title={title}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">{title}</h1>
          <button
            onClick={() => navigate("/add-new-project")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Dự án</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm dự án"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đơn vị chủ trì -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chọn cấp -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Dạng đề tài -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lĩnh vực -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Trạng thái -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chủ nhiệm -</option>
              </select>
            </div>

            {/* Row 3 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Thư ký -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lọc theo -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Từ năm -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đến năm -</option>
              </select>
              <div></div>
              <div></div>
            </div>

            {/* Row 4 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    STT
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Tên đề tài, nhiệm vụ
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200"
                    colSpan={2}
                  >
                    Thời gian thực hiện
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Cấp nhiệm vụ
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Chủ nhiệm nhiệm vụ
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Đơn vị chủ trì thực hiện
                  </th>
                  <th className="px-1 py-3 text-right text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Tổng kinh phí
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Nguồn kinh phí
                  </th>
                  <th
                    className="px-1 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200"
                    colSpan={2}
                  >
                    Kinh phí đã cấp
                  </th>
                  <th className="px-1 py-3 text-right text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Kinh phí chưa cấp
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Tiến độ
                  </th>
                  <th className="px-1 py-3 text-right text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    #
                  </th>
                  <th className="px-1 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-12">
                    Chức năng
                  </th>
                </tr>
                <tr className="border-b border-gray-200">
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Bắt đầu
                  </th>
                  <th className="px-1 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Kết thúc
                  </th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Kinh phí
                  </th>
                  <th className="px-1 py-2 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Năm cấp
                  </th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2 border-r border-gray-200"></th>
                  <th className="px-1 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-1 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.id}
                    </td>
                    <td className="px-1 py-4 text-sm text-gray-900 max-w-[96px] border-r border-gray-200">
                      <div className="mb-1">
                        <div className="font-medium text-[#337ab7] text-sm leading-tight">
                          {item.projectName}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        Trạng thái:{" "}
                        <span
                          className={
                            item.status === "Đúng tiến độ" ? "text-red-600" : ""
                          }
                        >
                          {item.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      {item.startDate}
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      {item.endDate}
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="space-y-0.5">
                        <div className="leading-tight text-sm">
                          {item.level}
                        </div>
                        {item.type && (
                          <div className="text-sm text-gray-600 leading-tight">
                            Dạng: {item.type}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="leading-tight text-sm">{item.owner}</div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 max-w-[80px] border-r border-gray-200">
                      <div className="leading-tight text-sm">{item.unit}</div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-right border-r border-gray-200">
                      <span className="text-red-600 font-semibold text-sm">
                        {item.totalBudget}
                      </span>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 max-w-[80px] border-r border-gray-200">
                      <div className="leading-tight text-sm">
                        {item.fundingSource}
                      </div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      <span className="text-red-600 font-semibold text-sm">
                        {item.allocatedBudget}
                      </span>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      <span className="text-sm">{item.allocationYear}</span>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-right border-r border-gray-200">
                      {item.unallocatedBudget ? (
                        <span className="text-red-600 font-semibold text-sm">
                          {item.unallocatedBudget}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="leading-tight text-sm">
                        {item.progress}
                      </div>
                    </td>
                    <td className="px-1 py-4 text-sm font-medium text-gray-900 text-right border-r border-gray-200">
                      <span className="text-sm">{item.reference}</span>
                    </td>
                    <td className="px-1 py-4 whitespace-nowrap border-r border-gray-200">
                      <button className="bg-[#337ab7] text-white px-2 py-1.5 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors">
                        <span className="text-sm">{item.function}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200 space-y-3 sm:space-y-0">
            <div className="text-sm font-medium text-gray-700 text-center sm:text-left">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default GenericPage;
